import React, { useEffect, useState } from 'react'
import { format } from 'date-fns'
import { DayPicker, MonthCaption } from 'react-day-picker'
import { ja, enUS } from 'react-day-picker/locale'
import { DayPickerProps } from './types'
import type { MonthProps } from 'react-day-picker'
import { Day } from './elements/Day'
import { Month } from './elements/Month'
import { Nav } from './elements/Nav'
import { DayButton } from './elements/DayButton'
import { useLpAvailabilityPageStore } from '../../store/lpAvailabilityPageStore'
import { useSearchStore } from '../../store/jpeTopPageStore'
import { formatPrice } from '../jpe-top-page/utils'
import { useTranslation } from 'react-i18next'

// 祝日の印を出さない画面（プラン詳細）用。components に渡すので、描画のたびに作り直さないよう外で定義する
const MonthWithoutHolidayCaption = (props: MonthProps) => <Month {...props} showHolidayCaption={false} />

const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number)
  return new Date(year, month - 1, day)
}

export const DatePicker = (props: DayPickerProps) => {
  const [selected, setSelected] = useState<Date>()
  const [currentMonth, setCurrentMonth] = useState<Date | undefined>(props.initDate)
  const { isJpe } = useLpAvailabilityPageStore()
  const { selectedCurrency } = useSearchStore()
  const { t } = useTranslation()
  const range = props.stayDay || 7
  const data = props.data
  const isFull = props.full
  const showDayIcon = typeof props.showDayIcon !== 'undefined' ? props.showDayIcon : !isFull
  const holidays = JSON.parse((document.querySelector('[data-holidays]') as HTMLElement)?.dataset?.holidays || '[]') as string[]

  // [FB-154] 在庫ステータスを考慮してハイライトの可否を決める。
  // 満室/受入不可（status='NG'）や在庫データに無い日付は「選択済み」の帯（startRange/endRange/inRange）を出さない。
  // DayButton は available でない日を disabled にしているため、満室日が強調されるのは initDate（検索の start_date）
  // 経由の selected のみ。ここで initDate を available な日に限定することで、満室日の初期ハイライトを防ぐ。
  const isHighlightable = (date?: Date): boolean => {
    if (!date) return false
    const dateString = Intl.DateTimeFormat('sv-SE').format(date)
    const item = data.find((d) => d.date === dateString)
    return !!item && (item.status === 'OK' || item.status === '-')
  }

  useEffect(() => {
    setSelected(isHighlightable(props.initDate) ? props.initDate : undefined)

    if (props.initDate) {
      setCurrentMonth(props.initDate)
      return
    }
    // Set month to calendar if select_month is exists
    if (!isFull && data.length) {
      const urlParams = new URLSearchParams(window.location.search)
      const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()[0] // "2025/08,2025/06,2025/10"
      const [year, monthStr] = selectedMonth.split('/')
      const date = new Date(parseInt(year), parseInt(monthStr) - 1)
      const startMonth = getStartMonth()
      setCurrentMonth(date && date >= startMonth ? date : startMonth)
    }
  }, [props.initDate, props.data])

  const getPrice = (date: Date): string => {
    const dateFormatter = Intl.DateTimeFormat('sv-SE')
    const dateString = dateFormatter.format(date)
    const price = data.find((item) => item.date === dateString)?.price
    if (!price) {
      return ''
    }

    if (isJpe) {
      const convertedPrice = formatPrice(parseInt(price), selectedCurrency)
      if (selectedCurrency === 'JPY') {
        return '¥' + parseInt(convertedPrice.replace(/[^\d]/g, '')) / 1000 + 'k'
      }
      return convertedPrice.replace(/[AC]\$/g, '$')
    }

    const man = parseInt(price) / 10000
    return String(man % 1 === 0 ? man.toFixed() : man.toFixed(1)) + t('reservation_confirm.modal_booking.currency_man')
  }

  const getEndRange = () => {
    if (selected) {
      const formatted = ((d) =>
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)(selected)
      // TODO: check many dates
      const selectedDate = data.find((d) => d.date == formatted)
      const selectedRange = selectedDate ? selectedDate.stayDay : range
      return new Date(selected.getTime() + (selectedRange - 1) * 24 * 60 * 60 * 1000)
    }

    return undefined
  }

  const getStartMonth = () => {
    const date = data[0] && parseLocalDate(data[0].date)

    if (!props.startMonth && date) {
      return date
    }

    if (props.startMonth && date && props.startMonth.getFullYear() == date.getFullYear() && date > props.startMonth) {
      return date
    }

    return props.startMonth || undefined
  }

  const getEndMonth = () => {
    const lastItem = data[data.length - 1]
    const date = lastItem && parseLocalDate(lastItem.date)

    if (
      (selected && !isFull) ||
      (selected &&
        isFull &&
        date &&
        (date.getMonth() < 3
          ? props.endMonth?.getFullYear() === date.getFullYear()
          : props.endMonth?.getFullYear() === date.getFullYear() + 1))
    ) {
      const selectedEndRange = new Date(selected.getTime() + (range - 1) * 24 * 60 * 60 * 1000)
      if (!props.endMonth && date && selectedEndRange > date) {
        return selectedEndRange
      }

      if (props.endMonth && (selectedEndRange > props.endMonth || selectedEndRange > date)) {
        return selectedEndRange
      }
    }

    if (!props.endMonth && date) {
      return date
    }

    if (
      props.endMonth &&
      date &&
      (date.getMonth() < 3
        ? props.endMonth.getFullYear() === date.getFullYear()
        : props.endMonth.getFullYear() === date.getFullYear() + 1)
    ) {
      return date
    }

    return props.endMonth || undefined
  }

  const getNumberOfMonths = () => {
    if (isFull && data.length > 0) {
      const first = getStartMonth()
      const last = getEndMonth()
      return last.getMonth() - first.getMonth() + 1 + (last.getFullYear() - first.getFullYear()) * 12
    }
    if (isFull && data.length === 0) {
      const urlParams = new URLSearchParams(window.location.search)
      const selectedMonth = (urlParams.get('select_month') || '').split(',').sort()
      const lastSelectedMonth = selectedMonth[selectedMonth.length - 1]
      if (lastSelectedMonth) {
        const lastSelectedMonthNumber = parseInt(lastSelectedMonth.split('/')[1])
        const currentMonthNumber = new Date().getMonth() + 1
        return Math.max(lastSelectedMonthNumber - currentMonthNumber + 2, 1)
      }
    }

    return 1
  }

  const modifiers = {
    listed: data.map((item) => parseLocalDate(item.date)),
    available: data.filter((item) => item.status === 'OK' || item.status === '-').map((item) => parseLocalDate(item.date)),
    ...(selected
      ? {
          inRange: { from: selected, to: getEndRange() },
          startRange: selected,
          endRange: getEndRange(),
        }
      : {}),
    holiday: holidays.map((item) => parseLocalDate(item)),
    forSale: data.filter((item) => item.status === '-').map((item) => parseLocalDate(item.date)),
  }

  const Blank = () => {
    return <></>
  }

  const handleOnSelect = (value) => {
    setSelected(value)
    if (props.onChangeDataSearch) props.onChangeDataSearch('start_date', value, 'text')
  }

  // Handle month change in the calendar
  const handleMonthChange = (month: Date) => {
    setCurrentMonth(month)
  }

  // md 未満は root をコンテナにして、マス（DayButton）を root の幅の 1/7 まで縮める（狭い画面で 7 列が入れ物からはみ出さないように）。
  // md 以上はコンテナにしない（中身の幅で決まる入れ物に置くと、幅が決まらずつぶれるため。例: プラン詳細のドロワーの md:w-fit）
  return (
    <>
      <DayPicker
        className="bg-white py-4 rounded-lg max-md:@container"
        mode="single"
        locale={isJpe ? enUS : ja}
        selected={selected}
        modifiers={modifiers}
        onSelect={handleOnSelect}
        month={isFull ? getStartMonth() : currentMonth || getStartMonth()}
        onMonthChange={handleMonthChange}
        numberOfMonths={getNumberOfMonths()}
        components={{
          Nav: isFull ? Blank : Nav,
          Month: props.showHolidayCaption === false ? MonthWithoutHolidayCaption : Month,
          Day,
          DayButton: (props) => <DayButton getPrice={getPrice} isShowIcon={showDayIcon} {...props} />,
          MonthCaption: isFull ? (props) => <MonthCaption {...props} className="mb-3 font-bold pb-3 mt-6 font-size-20" /> : Blank,
        }}
        formatters={{
          formatCaption: (date, options) => format(date, isJpe ? 'LLLL yyyy' : 'yyyy年LLLL', options),
        }}
        {...props}
        startMonth={getStartMonth()}
        endMonth={getEndMonth()}
      />
    </>
  )
}
